import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldAlert, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PHASES } from "@/lib/beta-checklist";

/**
 * Beta reports (admin). Every report testers sent from the Beta test page,
 * newest first; open one for its results, comments and the log it carried.
 *
 * Items are shown in checklist order with the title the TESTER saw (stored in
 * the report), so an old report still reads correctly after the checklist is
 * reworded.
 */

const listReports = () => api.get("/beta/reports").then((r) => r.data);
const getReport = (id) => api.get(`/beta/reports/${id}`).then((r) => r.data);

const ORDER = PHASES.flatMap((p) => p.items.map((i) => [i.id, p.name]));
const PHASE_OF = Object.fromEntries(ORDER);
const RANK = Object.fromEntries(ORDER.map(([id], i) => [id, i]));

const when = (s) => new Date(Number(s) * 1000).toISOString().replace("T", " ").slice(0, 16) + "Z";
const who = (r) => `${r.pid} ${[r.first_name, r.last_name].filter(Boolean).join(" ")}`.trim();
const tone = { pass: "text-emerald-600 dark:text-emerald-400", fail: "text-primary", skip: "text-amber-600 dark:text-amber-400" };

export default function BetaReports() {
  const { user } = useAuth();
  const [openId, setOpenId] = useState(null);
  const { data: reports = [], isLoading, error } = useQuery({
    queryKey: ["beta-reports"], queryFn: listReports, enabled: !!user?.is_admin, refetchInterval: 60_000,
  });

  if (!user?.is_admin) {
    return (
      <div className="p-8"><div className="card-flat p-10 text-center text-sm text-muted-foreground">
        <ShieldAlert className="w-6 h-6 mx-auto mb-2" />Beta reports are for admins.
      </div></div>
    );
  }

  return (
    <div className="p-8 max-w-[1400px]" data-testid="beta-reports-page">
      <div className="mb-6">
        <div className="overline mb-1">Admin</div>
        <h1 className="text-3xl font-bold tracking-tight">Beta reports</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          Test flights sent from the Beta test page. New reports are also posted to the beta Discord
          channel when one is configured.
        </p>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : error ? (
        <div className="card-flat p-6 text-sm text-primary">
          {error.response?.status === 500 || error.response?.status === 503
            ? "Reports could not be loaded. If this is a new server, migration 010_beta_reports.sql may not have been applied yet."
            : error.response?.data?.detail || "Reports could not be loaded."}
        </div>
      ) : reports.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-muted-foreground">No reports yet.</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
          <div className="card-flat divide-y divide-border self-start" data-testid="beta-report-list">
            {reports.map((r) => (
              <button key={r.id} type="button" onClick={() => setOpenId(r.id)}
                className={`w-full text-left px-4 py-3 hover:bg-secondary transition-colors ${openId === r.id ? "bg-secondary" : ""}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">#{r.id}</span>
                  <span className="font-semibold">{who(r)}</span>
                  <span className="text-muted-foreground">{r.setup_label}</span>
                  <span className="ml-auto font-mono text-xs text-muted-foreground">{when(r.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 text-xs font-mono tabular-nums mt-1">
                  <span className={tone.pass}>{r.n_pass} pass</span>
                  <span className={tone.fail}>{r.n_fail} fail</span>
                  <span className={tone.skip}>{r.n_skip} skip</span>
                  {r.n_open > 0 && <span className="text-muted-foreground">{r.n_open} not done</span>}
                  <span className="text-muted-foreground ml-auto">{r.app_version || "?"}{r.flight ? ` · ${r.flight}` : ""}</span>
                </div>
              </button>
            ))}
          </div>
          {openId ? <ReportDetail id={openId} /> : (
            <div className="card-flat p-10 text-sm text-muted-foreground text-center self-start">Choose a report to read it.</div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportDetail({ id }) {
  const { data: r, isLoading } = useQuery({ queryKey: ["beta-report", id], queryFn: () => getReport(id) });
  if (isLoading || !r) return <div className="card-flat p-6 text-sm text-muted-foreground self-start">Loading report…</div>;

  const items = Object.entries(r.results || {}).sort(([a], [b]) => (RANK[a] ?? 999) - (RANK[b] ?? 999));
  const fails = items.filter(([, v]) => v.status === "fail");
  const copyLog = async () => {
    try { await navigator.clipboard.writeText(r.log_excerpt || ""); toast.success("Log copied"); }
    catch { toast.error("Could not copy; select the log text instead"); }
  };

  return (
    <article className="card-flat p-5 space-y-5 self-start min-w-0" data-testid="beta-report-detail">
      <header>
        <div className="text-lg font-bold">#{r.id} · {who(r)}</div>
        <div className="text-sm text-muted-foreground">
          {r.setup_label} · {r.app_version || "version ?"} · checklist v{r.checklist_version} · {when(r.created_at)}
          {r.flight ? ` · ${r.flight}` : ""}{r.aircraft ? ` · ${r.aircraft}` : ""}{r.live_id ? ` · flight ${r.live_id}` : ""}
        </div>
      </header>

      {fails.length > 0 && (
        <section>
          <h2 className="overline mb-2">Failures</h2>
          <ul className="space-y-2">
            {fails.map(([k, v]) => (
              <li key={k} className="border-l-4 border-primary pl-3 text-sm">
                <div className="font-semibold">{v.t || k}</div>
                {v.note && <div className="whitespace-pre-wrap text-muted-foreground">{v.note}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {r.comments && (
        <section>
          <h2 className="overline mb-2">Comments</h2>
          <p className="text-sm whitespace-pre-wrap">{r.comments}</p>
        </section>
      )}

      <section>
        <h2 className="overline mb-2">All results</h2>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            {items.map(([k, v]) => (
              <tr key={k} className="align-top">
                <td className="py-1.5 pr-3 text-xs text-muted-foreground whitespace-nowrap">{PHASE_OF[k] || "—"}</td>
                <td className="py-1.5 pr-3">{v.t || k}{v.note && v.status !== "fail" ? <div className="text-xs text-muted-foreground whitespace-pre-wrap">{v.note}</div> : null}</td>
                <td className={`py-1.5 font-mono text-xs uppercase ${tone[v.status] || ""}`}>{v.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <div className="flex items-center mb-2">
          <h2 className="overline">Log</h2>
          {r.log_excerpt && (
            <button type="button" onClick={copyLog} className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copy log
            </button>
          )}
        </div>
        {r.log_excerpt ? (
          <pre className="text-[11px] leading-relaxed font-mono bg-secondary border border-border rounded-md p-3 max-h-96 overflow-auto whitespace-pre">{r.log_excerpt}</pre>
        ) : (
          <p className="text-sm text-muted-foreground">No log attached (sent from a browser, or the tester left it out).</p>
        )}
      </section>
    </article>
  );
}
